/** Деньги приходят СТРОКАМИ (MA-INV-6) и строками же и живут.
 *
 * `Number("1240.55")` в JS — это float, а float молча теряет копейки. Поэтому
 * парсим только там, где результат нужен для ПИКСЕЛЕЙ (ширина полосы,
 * координаты графика), и никогда — для того, что человек прочитает как сумму.
 */
const SYMBOL: Record<string, string> = { USD: "$", RUB: "₽", VND: "₫", EUR: "€" };

/** Неразрывный пробел, и разряды, и знак валюты. Обычный пробел позволил бы
 *  строке «1 240 $» переноситься посреди суммы — а половина суммы на конце
 *  строки читается как другая сумма. */
const NB = " ";

/** Сколько знаков после запятой показываем. Домен считает точно — 20/12 даёт
 *  1.666…7, — но на экране это шум: человек не платит треть копейки. Округляем
 *  ТОЛЬКО при показе, исходное значение не трогаем. */
const SCALE = 2n;

/** «1240.00», «USD» → «1 240 $». Дробную часть показываем, только если она есть. */
export function formatMoney(amount: string, currency: string): string {
  const negative = amount.trimStart().startsWith("-");
  const cents = roundToCents(amount);
  const whole = (cents / 100n).toString();
  const rest = cents % 100n;

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NB);
  const frac = rest === 0n ? "" : `,${rest.toString().padStart(2, "0").replace(/0$/, "")}`;
  const sym = SYMBOL[currency] ?? currency;
  return `${negative && cents !== 0n ? "−" : ""}${grouped}${frac}${NB}${sym}`;
}

/** «380000» → «380 000». Число с разрядами, но без валюты — для подписей
 *  графика, где валюта названа в заголовке, а не у каждой полоски. Та же
 *  разбивка и то же округление, что у суммы: «380000» рядом с «1 615,42» в
 *  одном ответе читались бы как числа разного порядка. */
export function formatNumber(amount: string): string {
  return formatMoney(amount, "").replace(new RegExp(`${NB}$`), "");
}

/** Модуль суммы в копейках, округлённый по третьему знаку. Через BigInt: на
 *  строках вроде «9007199254740993.01» число с плавающей точкой уже врёт. */
function roundToCents(amount: string): bigint {
  const [whole = "0", frac = ""] = amount.replace("-", "").trim().split(".");
  const digits = (frac + "000").slice(0, Number(SCALE) + 1);
  const scaled = BigInt(whole || "0") * 1000n + BigInt(digits || "0");
  const carry = scaled % 10n >= 5n ? 1n : 0n;   // округление вверх по третьему знаку
  return scaled / 10n + carry;
}

/** Доля для ширины полосы, 0..1+. Только для оформления — не для чисел на экране. */
export function ratio(part: string, whole: string): number {
  const w = Number(whole);
  if (!Number.isFinite(w) || w <= 0) return 0;
  return Number(part) / w;
}
